import * as Sentry from '@sentry/browser';
import { CANADA_POST } from '~/constants/carriers';
import * as moment from 'moment';
import Address, * as address from '~/types/Address';
import * as APIMessage from '~/types/APIMessage';
import Rate, * as rate from '~/types/Rate';
import Track, { TrackStatus } from '~/types/Track';

enum TransactionStatus {
  WAITING = 'WAITING',
  QUEUED = 'QUEUED',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
  REFUNDPENDING = 'REFUNDPENDING',
  REFUNDREJECTED = 'REFUNDREJECTED',
  REFUNDED = 'REFUNDED',
}

enum ObjectState {
  INVALID = 'INVALID',
  VALID = 'VALID',
}

type DeprecatedJSONTransactionProperties = {
  object_status?: string;
  was_test?: boolean;
};

type JSONTransaction = {
  object_state: string;
  status: string;
  object_status: string;
  object_created: string;
  object_updated: string;
  object_id: string;
  object_owner: string;
  test: boolean;
  rate: string;
  tracking_number: string;
  tracking_status: string;
  eta: Maybe<string>;
  tracking_url_provider: string;
  label_url: string;
  commercial_invoice_url: Maybe<string>;
  messages: APIMessage.JSONAPIMessage[];
  order: Maybe<string>;
  metadata: string;
  parcel: string;
  billing: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    payments: any[];
  };
} & DeprecatedJSONTransactionProperties;

type Transaction = {
  objectState: ObjectState;
  status: TransactionStatus;
  objectCreated: string;
  objectUpdated: string;
  objectId: string;
  objectOwner: string;
  test: boolean;
  rate: string;
  trackingNumber: string;
  trackingStatus: TrackStatus;
  eta: Maybe<string>;
  trackingUrlProvider: string;
  labelUrl: string; // TODO: This is wrong. Should actually be Maybe<string>
  commercialInvoiceUrl: Maybe<string>;
  messages: APIMessage.APIMessage[];
  order: Maybe<string>;
  metadata: string;
  parcel: string;
  billing: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    payments: any[];
  };
};

// Clean this up in the future; maps to the TransactionSerializerUI2 in Django
// only used for shipment modal on all orders page as of now
type JSONTransactionUI2 = {
  object_id: string;
  rate: rate.JSONRate;
  address_to: address.JSONAddress;
  order_number: string;
  was_test: boolean;
  tracking_url_provider: string;
  object_created: string;
  tracking_number: string;
  tracking_status: Track[];
  object_status: string;
};

type TransactionUI2 = {
  objectId: string;
  rate: Rate;
  addressTo: Maybe<Address>;
  orderNumber: string;
  wasTest: boolean;
  trackingUrlProvider: string;
  objectCreated: moment.Moment;
  trackingNumber: string;
  trackingStatus: Track[];
  objectStatus: TransactionStatus;
};

export type WebTransactionMessage = {
  code: string;
  text: string;
};

export type WebTransaction = {
  commercial_invoice_url: string;
  eta: string;
  entry_point: string;
  label_url: string;
  messages: WebTransactionMessage[];
  metadata: string;
  object_created: Date;
  object_updated: Date;
  object_id: string;
  object_owner: string;
  object_state: string;
  object_status: string;
  packing_slip_url: string;
  rate: rate.WebRate;
  refund_request_date: string;
  tracking_number: string;
  tracking_url_provider: string;
  was_test: boolean;
};

const RefundInitiatedTransactionStatuses: Partial<Record<TransactionStatus, boolean>> = {
  [TransactionStatus.REFUNDED]: true,
  [TransactionStatus.REFUNDPENDING]: true,
  [TransactionStatus.REFUNDREJECTED]: true,
};

function isTransactionStatusRefundable(transactionStatus: TransactionStatus | undefined) {
  return !!transactionStatus && !RefundInitiatedTransactionStatuses[transactionStatus];
}

function getLabelUrl(labelUrl: Maybe<string>): string {
  if (!labelUrl) return '';
  // Turns
  // https://local-delivery-shippodev-com.s3.us-east-1.amazonaws.com/b199fdf61e244027825cf3f3e2b3b0ca.pdf?Signature=3y31%2Br%2Fm1WhSdCw3f%2FPtPU1hFDI%3D&Expires=1605315319&AWSAccessKeyId=AKIAJWTIV4ATYNCRWP6Q
  // Into this:
  // https://delivery.goshippo.com/b199fdf61e244027825cf3f3e2b3b0ca.pdf?Signature=3y31%2Br%2Fm1WhSdCw3f%2FPtPU1hFDI%3D&Expires=1605315319&AWSAccessKeyId=AKIAJWTIV4ATYNCRWP6Q
  const thing = window.location.host;
  const parts = thing.split('.');
  const subdomain = parts[0];

  if (labelUrl.includes('local') || labelUrl.includes('stage-perf')) return labelUrl;

  if (!labelUrl.includes('amazonaws.com')) {
    return labelUrl;
  }

  try {
    const newSubdomain = subdomain.replace('apps', 'deliver');
    const newHost = `${newSubdomain}.${parts[1]}.${parts[2].split(':')[0]}`;
    const parsedUrl = new URL(labelUrl);
    const path = parsedUrl.pathname;
    const qs = parsedUrl.search;
    const isWindows = /Windows/.test(window.navigator.userAgent);
    const extParam = isWindows && path.includes('.pdf') ? '&ext=.pdf' : '';

    return `https://${newHost}${path}${qs}${extParam}`;
  } catch (err) {
    Sentry.captureException(err);
    return '';
  }
}

function fromJSON(jsonTransaction: JSONTransaction): Transaction {
  const test = (jsonTransaction.test ? jsonTransaction.test : jsonTransaction.was_test) || false;

  return {
    test,
    objectState: <ObjectState>jsonTransaction.object_state,
    status:
      <TransactionStatus>jsonTransaction.status || <TransactionStatus>jsonTransaction.object_status,
    objectCreated: jsonTransaction.object_created,
    objectUpdated: jsonTransaction.object_updated,
    objectId: jsonTransaction.object_id,
    objectOwner: jsonTransaction.object_owner,
    rate: jsonTransaction.rate,
    trackingNumber: jsonTransaction.tracking_number,
    trackingStatus: <TrackStatus>jsonTransaction.tracking_status,
    eta: jsonTransaction.eta,
    trackingUrlProvider: jsonTransaction.tracking_url_provider,
    labelUrl: getLabelUrl(jsonTransaction.label_url),
    commercialInvoiceUrl: jsonTransaction.commercial_invoice_url,
    messages: (jsonTransaction.messages || []).map(APIMessage.fromJSON),
    order: jsonTransaction.order,
    metadata: jsonTransaction.metadata,
    parcel: jsonTransaction.parcel,
    billing: jsonTransaction.billing,
  };
}

export function fromWebTransaction(webTransaction: WebTransaction): Transaction {
  const messages = webTransaction.messages.map(
    ({ code, text }): APIMessage.APIMessage => ({
      kind: 'APIMessage',
      source: '',
      code,
      text,
    }),
  );

  return {
    objectState: <ObjectState>webTransaction.object_state,
    status: <TransactionStatus>webTransaction.object_status,
    objectCreated: webTransaction.object_created.toISOString(),
    objectUpdated: webTransaction.object_updated.toISOString(),
    objectId: webTransaction.object_id,
    objectOwner: webTransaction.object_owner,
    test: webTransaction.was_test,
    rate: webTransaction.rate?.object_id,
    trackingNumber: webTransaction.tracking_number,
    eta: webTransaction.eta,
    trackingUrlProvider: webTransaction.tracking_url_provider,
    labelUrl: webTransaction.label_url,
    metadata: webTransaction.metadata,
    messages,

    commercialInvoiceUrl: webTransaction.commercial_invoice_url,
    trackingStatus: TrackStatus.UNKNOWN,
    order: undefined,
    parcel: '',
    billing: {
      payments: [],
    },
  };
}

function transactionUI2FromJSON(jsonTransaction: JSONTransactionUI2): TransactionUI2 {
  return {
    objectId: jsonTransaction.object_id,
    rate: rate.fromJSON(jsonTransaction.rate),
    addressTo: address.fromJSON(jsonTransaction.address_to),
    orderNumber: jsonTransaction.order_number,
    wasTest: jsonTransaction.was_test,
    trackingUrlProvider: jsonTransaction.tracking_url_provider,
    objectCreated: moment(jsonTransaction.object_created),
    trackingNumber: jsonTransaction.tracking_number,
    trackingStatus: jsonTransaction.tracking_status,
    objectStatus: <TransactionStatus>jsonTransaction.object_status,
  };
}

function isTransactionCompleted(status: TransactionStatus) {
  return new Set([TransactionStatus.SUCCESS, TransactionStatus.ERROR]).has(status);
}

function hasTransactionError(transaction: Transaction) {
  return transaction.status === TransactionStatus.ERROR && transaction.messages.length > 0;
}

function isRefundIneligible(transaction: TransactionUI2) {
  // If carrier is Canada Post and the txn was created
  // less than 45 minutes ago, then the label is
  // currently ineligible for refund.
  return (
    transaction.rate.provider === CANADA_POST.name &&
    transaction.objectCreated.isAfter(moment().subtract(60, 'minute'))
  );
}

function isIneligibleForPaperlessTrade(transaction: Transaction) {
  return (
    !!transaction.commercialInvoiceUrl &&
    transaction.messages.length > 0 &&
    !!transaction.messages.find((x) => x.text.includes('Not eligible for paperless trade.'))
  );
}

function getValidSubmitTime(transaction: TransactionUI2) {
  const offset = new Date().getTimezoneOffset();
  return moment.utc(transaction.objectCreated).utcOffset(offset).add(60, 'minute').local();
}

export default Transaction;
export {
  RefundInitiatedTransactionStatuses,
  Transaction,
  TransactionUI2,
  TransactionStatus,
  fromJSON,
  transactionUI2FromJSON,
  JSONTransaction,
  JSONTransactionUI2,
  ObjectState,
  isTransactionCompleted,
  hasTransactionError,
  isRefundIneligible,
  getValidSubmitTime,
  getLabelUrl,
  isIneligibleForPaperlessTrade,
  isTransactionStatusRefundable,
};
